import { SxProps, Theme } from '@mui/material';
import { APP_BAR_HEIGHT } from '../../layout/header.styles';
import { brandAlpha, brandColors, hoverOnly } from "../../../theme/mixins";

/** How tall one hour is. Everything on the grid is positioned from this. */
export const HOUR_HEIGHT = 72;

/** How wide the hour-label column is. The lane starts where it ends. */
export const LABEL_COLUMN = 64;

/**
 * The narrowest a day column may be before the grid starts scrolling instead of squeezing.
 *
 * Self-regulating, which is why there is no breakpoint anywhere near it: a week is
 * `64 + 7 x 132 = 988px`, which fits inside the layout's `maxWidth="lg"` container on a
 * desktop and never fits on a phone. A day is one column and so never scrolls at all.
 */
export const WEEK_COLUMN_MIN = 132;

/**
 * Below this, a card only has room for two lines.
 *
 * Three lines of text plus the usual padding come to about 53px, so a half-hour session --
 * 36px at this scale, and the clinic's commonest booking -- cannot hold them. It used to
 * print all three anyway and clip the last one mid-letter, which is how a patient's name
 * ended up sliced in half. The third line is dropped instead, and the padding tightens, so
 * what is left is whole.
 */
export const SHORT_CARD_MAX = 52;

/**
 * Below this, a card only has room for one line, and that line is the patient's name.
 *
 * A twenty-minute session is 24px, floored to the 28px that keeps it clickable, and two
 * lines need about 35. The time is what gets dropped, because it is the one fact the card
 * does not have to state -- where it sits on an hour axis already says it.
 */
export const ONE_LINE_MAX = 34;

export const headerBoxStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 2,
    mb: 3,
};

export const titleStyles: SxProps<Theme> = {
    fontWeight: 700,
};

/**
 * The view toggle, the pager and the add button.
 *
 * Wraps, because on a phone it is wider than the screen and the alternative is the page
 * scrolling sideways -- which is the one thing the grid's own scroller exists to avoid.
 */
export const dayControlsStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 1,
};

/**
 * The pager -- back arrow, date, forward arrow -- held together as one unit.
 *
 * The row around it wraps on purpose, but wrapping treated these three as three separate
 * items, so at 375px the date broke onto its own line with one arrow above it and the
 * other below. Two arrows pointing in opposite directions on opposite rows read as two
 * unrelated controls rather than as a pager.
 *
 * `nowrap` here does not risk the sideways scroll the outer wrap exists to prevent: the
 * arrows are fixed and small, and `minWidth: 0` lets the date -- the only elastic part --
 * take a second line inside the group instead of pushing the group wider than the screen.
 */
export const dayPagerStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 1,
    minWidth: 0,
};

/** Never squeezed: it is the date beside them that gives way. */
export const dayPagerArrowStyles: SxProps<Theme> = {
    flexShrink: 0,
    minWidth: 0,
};

/**
 * The fixed width keeps the pager arrows still as the date changes under them, which is
 * worth a lot when clicking through several days. It is dropped on a phone, where 220px of
 * mostly-empty label is what pushes the row off the screen.
 */
export const dayLabelStyles: SxProps<Theme> = {
    minWidth: { xs: 0, sm: 220 },
    textAlign: 'center',
    fontWeight: 600,
};

/**
 * The columns every part of the grid lines up on.
 *
 * The header row and the hour rows are two separate grids -- the lane container is taken
 * out of flow and would otherwise cover the headers -- so they have to be handed the same
 * template, which is what this is for.
 */
const columnTemplate = (columns: number): string =>
    columns === 1
        ? `${LABEL_COLUMN}px 1fr`
        : `${LABEL_COLUMN}px repeat(${columns}, minmax(${WEEK_COLUMN_MIN}px, 1fr))`;

export const gridStyles = (columns: number): SxProps<Theme> => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: columnTemplate(columns),
    border: '1px solid rgba(16,24,40,0.08)',
    borderRadius: 2,
    // Square off the top when day headers sit above it, so the two read as one card.
    ...(columns === 1 ? {} : { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 0 }),
    overflow: 'hidden',
    backgroundColor: 'background.paper',
});

/**
 * Holds the day-name rail and the grid together, and is the reason the rail lets go.
 *
 * A sticky element is only sticky for as long as its containing block is on screen. Left
 * as siblings under the page container these two would have the whole page for a block,
 * so the weekday names would still be hovering over the footer long after the last hour
 * had scrolled past. Wrapped, the rail is released the moment the grid's bottom is.
 */
export const weekFrameStyles: SxProps<Theme> = {
    position: 'relative',
    minWidth: 0,
};

/**
 * The strip that keeps the weekday names on screen as the page scrolls down.
 *
 * It sits *outside* the sideways scroller rather than inside it, which is the whole trick.
 * `position: sticky` is measured against the nearest scroll container, and inside the
 * scroller that is a box with no vertical scrolling to do -- so the header stuck to
 * nothing and scrolled away with the page, which is exactly what was reported. Out here
 * the page is what it sticks to, and the sideways half of the movement it lost is given
 * back by `useMirroredScrollLeft`, which slides the track inside to match the grid's own
 * `scrollLeft`.
 *
 * `top` clears the app bar, which is sticky itself and would otherwise cover this.
 *
 * `clip` and not `hidden` for the overflow, for a reason particular to what is inside:
 * every day name is a button, `hidden` leaves the box scrollable programmatically, and a
 * browser revealing a focused button in a box nobody can see scrolling would shunt the
 * names out of step with the columns they name. `clip` cannot be scrolled at all.
 */
export const dayHeaderRailStyles: SxProps<Theme> = {
    position: 'sticky',
    top: APP_BAR_HEIGHT,
    // Above the now-line and the cards, which the grid draws at 1 and 0 -- the header has
    // to cover them as they pass under it, not the other way round.
    zIndex: 2,
    overflow: 'clip',
    backgroundColor: 'background.paper',
};

export const dayHeaderRowStyles = (columns: number): SxProps<Theme> => ({
    display: 'grid',
    gridTemplateColumns: columnTemplate(columns),
    border: '1px solid rgba(16,24,40,0.08)',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    backgroundColor: 'background.paper',
});

/**
 * One weekday above its column, and the way into that day on its own.
 *
 * A button rather than a label because a therapist who spots a heavy Thursday wants to be
 * looking at Thursday, and there is nowhere else on the page to ask for it. Today is
 * marked in weight and colour *and* keeps its date in the text, so the emphasis is never
 * the only thing saying which day it is.
 */
export const dayHeaderStyles = (isToday: boolean): SxProps<Theme> => ({
    py: 1,
    px: 0.5,
    minWidth: 0,
    borderRadius: 0,
    borderLeft: '1px solid rgba(16,24,40,0.08)',
    fontSize: '0.78rem',
    lineHeight: 1.3,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: isToday ? 700 : 500,
    color: isToday ? 'primary.main' : 'text.secondary',
});

/** The corner above the hour gutter. Empty, and never a target. */
export const dayHeaderGutterStyles: SxProps<Theme> = {
    borderRight: '1px solid rgba(16,24,40,0.08)',
};

export const hourLabelStyles: SxProps<Theme> = {
    height: HOUR_HEIGHT,
    pr: 1,
    pt: 0.5,
    textAlign: 'right',
    fontSize: '0.75rem',
    color: 'text.secondary',
    borderTop: '1px solid rgba(16,24,40,0.06)',
    borderRight: '1px solid rgba(16,24,40,0.08)',
};

/**
 * One empty hour cell.
 *
 * `withDivider` draws the line between two day columns. It is off for the first column
 * because the hour gutter already draws its own right-hand edge, and two rules a pixel
 * apart read as a thick smudge rather than a border.
 */
export const hourSlotStyles = (withDivider: boolean): SxProps<Theme> => ({
    height: HOUR_HEIGHT,
    borderTop: '1px solid rgba(16,24,40,0.06)',
    ...(withDivider ? { borderLeft: '1px solid rgba(16,24,40,0.08)' } : {}),
});

/**
 * One empty half hour, and the way into booking it.
 *
 * These are drawn *inside* the lane rather than on the hour cells underneath, because
 * `laneRowStyles` is absolute over the whole day area and a click can never reach the cells
 * it covers. Drawn before the cards, so a card always paints over one and takes its own
 * click -- and one is not drawn at all where something occupies the time, so what is
 * clickable is exactly what looks empty.
 *
 * Half an hour rather than a whole one because that is `DIARY_START_STEP_MINUTES`, the step
 * the diary already offers: the clinic books on the hour and the half hour, and a grid that
 * could only say the o'clocks would be narrower than the form it opens.
 *
 * The tint goes through `hoverOnly`, like every other hover in this codebase: a touch
 * browser leaves a faked hover on the last-tapped element, so an unguarded one would sit
 * highlighted on a slot the therapist has finished with.
 */
export const emptySlotStyles = (topPx: number, heightPx: number): SxProps<Theme> => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: topPx,
    height: heightPx,
    cursor: 'pointer',
    ...hoverOnly({ backgroundColor: brandAlpha('primary', 0.06) }),
});

/**
 * The strip the day columns live in.
 *
 * Taken out of flow with `position: absolute` rather than placed as a grid child. As a
 * child it competed for auto-placement with the hour rows and pushed every label after the
 * first into the wrong column -- an absolutely positioned box is removed from the grid
 * entirely, so the rows lay themselves out as if it were not there.
 *
 * Flex inside it, so N equal lanes fall out without anybody having to know what a column
 * is worth in pixels -- which nothing here does, the template being `1fr`-based.
 */
export const laneRowStyles: SxProps<Theme> = {
    position: 'absolute',
    left: LABEL_COLUMN,
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
};

/** One day's share of that strip. Cards inside are positioned against it. */
export const laneStyles: SxProps<Theme> = {
    position: 'relative',
    flex: 1,
    minWidth: 0,
};

/**
 * The part of a column that has already gone, washed over.
 *
 * Covers the hour rules and sits under the lanes so that each card can wash itself -- see
 * `pastVeil`. Doing it in two places rather than one big overlay is what lets a card keep
 * its status border crisp while its inside fades.
 *
 * `left`/`width` are CSS lengths rather than numbers because a week's columns are only
 * ever known as a fraction: the caller hands over a `calc()` against the grid's own width.
 * A day view passes the full width, gutter included, which is what it has always shaded.
 */
export const pastShadeStyles = (
    heightPx: number,
    left: string,
    width: string
): SxProps<Theme> => ({
    position: 'absolute',
    left,
    width,
    top: 0,
    height: heightPx,
    backgroundColor: 'rgba(16,24,40,0.075)',
    pointerEvents: 'none',
});

/**
 * The same wash, over a card that is already over.
 *
 * A veil rather than `opacity`, and one that goes *under* the card's own text rather than
 * over it. Bleaching the whole card was the obvious way to do this and the wrong one: at a
 * wash strong enough to see, it took the patient's name down to 2.3:1 against the card,
 * which is well under what anybody could comfortably read. Washing only the background and
 * dropping the text to the theme's secondary grey holds it at about 5.4:1 -- muted at a
 * glance, still legible when you actually look.
 *
 * `inset: 0` is the padding box, so the 4px status border is untouched and held, no-show
 * and scheduled stay tellable apart. The ring on an unanswered session is a box-shadow,
 * outside the border box, so it survives too -- the point of washing the past is to make
 * the one thing still asking for attention stand out of it.
 *
 * The colour is the shaded canvas's own, so a spent card sinks into the background rather
 * than turning into a pale hole in it.
 */
const pastWash = {
    // Contains the veil and the content so neither escapes into the grid's stacking order.
    isolation: 'isolate',
    color: 'text.secondary',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(237,238,239,0.85)',
        pointerEvents: 'none',
        zIndex: 0,
    },
    '& > *': {
        position: 'relative',
        zIndex: 1,
    },
} as const;

/**
 * Where the clinic is in its day.
 *
 * Red because every other colour on this grid already means a status -- blue is scheduled,
 * green held, amber a no-show -- and because a red now-line is what every calendar the
 * therapist has ever used draws. The dot sits where the lane begins, so the line reads as
 * belonging to the appointments rather than to the hour labels it crosses.
 *
 * In a week it is confined to today's column. Drawn across all seven it would be saying
 * something about Thursday that is only true of Tuesday.
 */
export const nowLineStyles = (
    topPx: number,
    left: string,
    width: string,
    dotLeftPx: number
): SxProps<Theme> => ({
    position: 'absolute',
    left,
    width,
    top: topPx,
    height: 0,
    borderTop: '2px solid',
    borderTopColor: 'error.main',
    zIndex: 1,
    pointerEvents: 'none',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: dotLeftPx,
        top: -5,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'error.main',
    },
});

/** How far a card is held off each edge of its column. */
const CARD_INSET = 8;

/** The channel between two cards sharing an hour, so the split reads as two cards. */
const CARD_GAP = 4;

/** Which of the lanes sharing an hour a card sits in — see `layoutLanes`. */
export interface LanePlacement {
    lane: number;
    lanes: number;
}

/** The ordinary case: nothing else is on these hours, so the card takes the column. */
export const SINGLE_LANE: LanePlacement = { lane: 0, lanes: 1 };

/**
 * A card's width and offset across its column.
 *
 * Everything here is a fraction of the column rather than a pixel count, because a week's
 * columns are `1fr` and nothing on this side knows what one is worth. The gaps are taken
 * out of the track *before* it is divided, so every lane is the same width and the last
 * one still lands on the column's own inset -- dividing first and then trimming each card
 * would leave a stray channel hanging off the right-hand edge.
 *
 * With one lane it reduces to exactly what it always was: inset on both sides, full width.
 */
const lanePlacement = ({ lane, lanes }: LanePlacement): { left: string; width: string } => {
    const track = `((100% - ${2 * CARD_INSET}px - ${(lanes - 1) * CARD_GAP}px) / ${lanes})`;

    return {
        left: `calc(${CARD_INSET}px + (${track} + ${CARD_GAP}px) * ${lane})`,
        width: `calc(${track})`,
    };
};

/**
 * One appointment, placed by its own start and length.
 *
 * A cancelled row stays on the grid rather than vanishing: it is deliberately faded and
 * struck through, because "this was called off" is something a therapist looking back at
 * their week needs to be able to see.
 *
 * `needsResolving` is orthogonal to the tone rather than a fifth one: it says the session
 * is over and nobody has said what happened. It rings the card instead of recolouring the
 * left border, which is the status carrier -- amber there already means a no-show.
 *
 * The two flags travel as an object rather than as a fourth and fifth argument: adjacent
 * booleans at a call site say nothing about which is which.
 */
export const appointmentStyles = (
    topPx: number,
    heightPx: number,
    tone: AppointmentTone,
    state: { isPast: boolean; needsResolving: boolean },
    lane: LanePlacement = SINGLE_LANE
): SxProps<Theme> => ({
    position: 'absolute',
    ...lanePlacement(lane),
    top: topPx,
    // Never collapse to nothing: a very short appointment still has to be clickable.
    height: Math.max(heightPx, 28),
    px: 1.5,
    py: heightPx <= SHORT_CARD_MAX ? 0.25 : 0.75,
    borderRadius: 1.5,
    overflow: 'hidden',
    cursor: 'pointer',
    borderLeft: '4px solid',
    ...{
        scheduled: {
            backgroundColor: brandAlpha('primary', 0.08),
            borderLeftColor: brandColors.primary,
        },
        held: {
            backgroundColor: 'rgba(22,163,74,0.10)',
            borderLeftColor: '#16a34a',
        },
        noShow: {
            backgroundColor: 'rgba(217,119,6,0.10)',
            borderLeftColor: '#d97706',
        },
        cancelled: {
            backgroundColor: 'rgba(16,24,40,0.04)',
            borderLeftColor: 'rgba(16,24,40,0.25)',
            opacity: 0.65,
            textDecoration: 'line-through',
        },
        // A patient's request, not yet agreed: the booking's own colour, but drawn in
        // outline -- dashed all round, and fainter inside -- so it reads as a pencilled-in
        // hour that still needs the therapist's answer rather than a settled one.
        requested: {
            backgroundColor: brandAlpha('primary', 0.03),
            borderLeftColor: brandColors.primary,
            borderLeftStyle: 'dashed',
            outline: `1px dashed ${brandColors.primary}`,
            outlineOffset: '-1px',
        },
    }[tone],
    // Deliberately heavier than it needs to be on a bright card: it has to survive the
    // wash, because a washed card is exactly the one this can appear on.
    ...(state.needsResolving ? { boxShadow: '0 0 0 2px rgba(217,119,6,0.85)' } : {}),
    ...(state.isPast ? pastWash : {}),
});

/** How a card is coloured: one per status, except that a declined request reads as cancelled. */
export type AppointmentTone = 'scheduled' | 'held' | 'noShow' | 'cancelled' | 'requested';

/**
 * A block of leave: no patient, so it reads as hatched dead time rather than a booking.
 *
 * Clickable all the same, because a block entered by mistake has to be removable and this
 * is the only place it appears.
 */
export const timeBlockStyles = (
    topPx: number,
    heightPx: number,
    isPast: boolean,
    lane: LanePlacement = SINGLE_LANE
): SxProps<Theme> => ({
    position: 'absolute',
    ...lanePlacement(lane),
    top: topPx,
    height: Math.max(heightPx, 28),
    px: 1.5,
    py: heightPx <= SHORT_CARD_MAX ? 0.25 : 0.75,
    borderRadius: 1.5,
    overflow: 'hidden',
    cursor: 'pointer',
    color: 'text.secondary',
    backgroundImage:
        'repeating-linear-gradient(45deg, rgba(16,24,40,0.05) 0 8px, rgba(16,24,40,0.02) 8px 16px)',
    border: '1px dashed rgba(16,24,40,0.2)',
    ...(isPast ? pastWash : {}),
});

/**
 * The first line of a card.
 *
 * Held to one line. Left to wrap it takes the second line's room in a week column and the
 * patient's name disappears off the bottom of the card -- the one thing on it that must
 * always be readable.
 */
export const appointmentTimeStyles: SxProps<Theme> = {
    fontSize: '0.72rem',
    lineHeight: 1.25,
    fontWeight: 600,
    opacity: 0.8,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const appointmentTitleStyles: SxProps<Theme> = {
    fontSize: '0.85rem',
    lineHeight: 1.25,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const appointmentServiceStyles: SxProps<Theme> = {
    fontSize: '0.75rem',
    lineHeight: 1.25,
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const emptyStateStyles: SxProps<Theme> = {
    py: 6,
    textAlign: 'center',
    color: 'text.secondary',
};

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 6,
};

export const detailRowStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 1,
    flexWrap: 'wrap',
    mt: 2,
};

export const viewToggleStyles: SxProps<Theme> = {
    mr: 1,
};

/**
 * The "show cancelled" switch.
 *
 * Last in the controls row and quieter than the rest of it: it answers a question a
 * therapist asks occasionally -- what happened to that hour -- rather than one they ask
 * every time they open the page. `ml: 'auto'` pushes it to the far end where there is
 * room, and gives it up the moment the row wraps on a phone.
 */
export const showCancelledToggleStyles: SxProps<Theme> = {
    ml: { xs: 0, sm: 'auto' },
    mr: 0,
    '& .MuiFormControlLabel-label': {
        fontSize: '0.875rem',
        color: 'text.secondary',
    },
};

/*
 * The scroller below follows `components/dashboard/chartScroller.styles.ts`, which solved
 * the same problem for a twelve-month chart. It is copied rather than imported: that one
 * is welded to the dashboard's own track formula and its "scroll to see the whole year"
 * hint, and `components/dashboard/` is for the pieces that draw a figure -- page chrome is
 * deliberately not shared. The rules are what matter, and they are the same rules.
 */

/**
 * The scrolling viewport. Only this scrolls -- the page never does, which is the promise
 * being kept here. `minWidth: 0` is what lets a grid or flex child shrink below its own
 * content instead of forcing its parent wider.
 */
export const scrollViewportStyles: SxProps<Theme> = {
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    minWidth: 0,
    maxWidth: '100%',
};

export const scrollTrackStyles = (minWidth: number): SxProps<Theme> => ({
    minWidth,
});

/**
 * Whether this is shown at all is now a measurement, not a breakpoint -- see
 * `useHorizontalOverflow`. The week grid needs 988px, so the old `sm` cutoff hid the hint
 * at 768px, where the grid very much still scrolls.
 */
export const scrollHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mt: 0.5,
};
